import React, { useMemo } from 'react';
import clsx from 'clsx';

import { useTable, useExpanded, useSortBy } from 'react-table';
import { Carets, CaretDown } from '@td-client-frontend/icons';

interface TableProps {
  columns: any[];
  data: any[];
}

export const Table = ({ columns, data }: TableProps) => {
  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } = useTable(
    {
      columns,
      data,
      initialState: {
        sortBy: [
          {
            id: 'firstName'
          }
        ]
      }
    },
    useSortBy,
    useExpanded
  );
  const columnNumber = columns.length - 1;

  const rowsTableData = useMemo(() => {
    if (!rows.length) {
      return (
        <tr className="text-center">
          <td colSpan={columnNumber} className="py-5">
            <h3>No data to display</h3>
          </td>
        </tr>
      );
    }

    let isGray = false;

    return rows.map((row, i) => {
      if (!row.depth) {
        isGray = !isGray;
      }
      prepareRow(row);
      return (
        <tr
          {...row.getRowProps()}
          key={row.id}
          className={clsx(
            isGray && 'bg-gray-10',
            !row.depth && 'border-t-2 border-gray-30',
            i + 1 === rows.length && 'border-b-2 border-gray-30'
          )}
        >
          {row.cells.map((cell) => {
            return (
              <td {...cell.getCellProps()} key={`table-data-${cell.row.id}`} className="break-all max-w-12 px-1 py-2">
                {cell.render('Cell')}
              </td>
            );
          })}
        </tr>
      );
    });
  }, [columnNumber, prepareRow, rows]);

  return (
    <table {...getTableProps()} className="w-full font-effra">
      <thead className="border-b-2 border-gray-30">
        {headerGroups.map((headerGroup) => (
          <tr {...headerGroup.getHeaderGroupProps()} key={`table-row-${headerGroup.id}`}>
            {headerGroup.headers.map((column) => (
              <th
                {...column.getHeaderProps(column.getSortByToggleProps())}
                key={`table-head-${column.id}`}
                className={clsx(
                  column.isSorted ? 'font-bold' : 'font-normal',
                  column.canSort && 'hover:text-gray-30',
                  'px-1 py-2 leading-loose tracking-wider text-left uppercase select-none text-gray-40'
                )}
              >
                {column.render('Header')}
                <span>
                  {column.canSort &&
                    (column.isSorted ? (
                      <CaretDown
                        className={clsx(
                          'w-5 h-5 transform inline ml-2',
                          column.isSortedDesc ? 'rotate-0' : 'rotate-180'
                        )}
                      />
                    ) : (
                      <Carets className="inline w-5 h-5 ml-2" />
                    ))}
                </span>
              </th>
            ))}
          </tr>
        ))}
      </thead>

      <tbody {...getTableBodyProps()}>{rowsTableData}</tbody>
    </table>
  );
};
